import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from "react-router-dom";
import { NotificationManager } from 'react-notifications';
import Header from 'src/layouts/Header';
import Footer from 'src/layouts/Footer';
import verifiedIcon from 'src/assets/svg/verified.svg';
import useSEO from 'src/hooks/useSEO';
import axios from 'src/api/axios';

const EmailVerified = () => {
  useSEO({
    title: 'Email Verification - SphynxPlace.com',
    description: 'Verifying your email address.',
    robots: 'noindex, nofollow',
  });
  
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('Verifying email...');
  const [progress, setProgress] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [isError, setIsError] = useState(false);
  const [alreadyVerified, setAlreadyVerified] = useState(false);
  const hasVerified = useRef(false);

  useEffect(() => {
    // Prevent double execution in React StrictMode
    if (hasVerified.current) return;
    hasVerified.current = true;

    const verifyEmail = async () => {
      const email = searchParams.get('email');
      const hash = searchParams.get('hash');
      const expires = searchParams.get('expires');
      const signature = searchParams.get('signature');

      // Check if we have all required params
      if (!email || !hash || !expires || !signature) {
        setStatus('Invalid verification link');
        setIsError(true);
        setProgress(100);
        return;
      }

      try {
        setStatus('Verifying email...');
        setProgress(30);

        // Short pause for smooth animation
        await new Promise(resolve => setTimeout(resolve, 300));
        
        setProgress(50);

        const response = await axios.post('/api/email/verify', {
          email,
          hash,
          expires: parseInt(expires, 10),
          signature
        }, { disableNotification: true });

        setProgress(80);
        
        await new Promise(resolve => setTimeout(resolve, 300));

        if (response.data.already_verified) {
          setStatus('Email Already Verified');
          setAlreadyVerified(true);
          NotificationManager.info('Your email is already verified.');
        } else {
          setStatus('Email Verified Successfully!');
          NotificationManager.success('Email verified successfully!');
        }
        
        setProgress(100);
        setIsComplete(true);

      } catch (error) {
        console.error('Email verification failed:', error);
        setProgress(100);
        setIsError(true);
        
        const errorMessage = error.response?.data?.error || 'Verification failed. Please try again.';
        setStatus(errorMessage);
        NotificationManager.error(errorMessage);
      }
    };

    verifyEmail();
  }, [searchParams]);

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <div className="bg-[#F7F7F7] flex-1 flex items-center justify-center py-4">
        <div className="container mx-auto px-4 w-full flex justify-center">
          <div className="max-w-[600px] w-full text-center animate-fadeIn">
            {!isComplete && !isError ? (
              <>
                {/* Spinner */}
                <div className="flex justify-center mb-8">
                  <div className="relative">
                    <div className="w-24 h-24 border-4 border-purple-100 rounded-full"></div>
                    <div className="w-24 h-24 border-4 border-[#832EE6] border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
                  </div>
                </div>
                
                {/* Status Message */}
                <h2 className="text-[24px] md:text-[32px] font-bold text-[#0D0D0D] mb-4">
                  {status}
                </h2>
                
                <p className="text-gray-600 mb-8 text-sm md:text-base leading-relaxed">
                  Please wait while we verify your email address...
                </p>
                
                {/* Progress Bar */}
                <div className="max-w-md mx-auto mb-4">
                  <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                    <div 
                      className="bg-[#832EE6] h-2 rounded-full transition-all duration-500 ease-out"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                  <p className="text-sm text-gray-500">
                    {progress}%
                  </p>
                </div>
              </>
            ) : (
              <>
                {/* Result Icon */}
                <div className="mb-6">
                  {isError ? (
                    <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                      <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </div>
                  ) : (
                    <img 
                      src={verifiedIcon} 
                      alt="Verified" 
                      className="w-20 h-20 mx-auto mb-4"
                    />
                  )}
                </div>
                
                <h1 className={`text-3xl font-bold mb-4 ${isError ? 'text-red-500' : 'text-[#832EE6]'}`}>
                  {status}
                </h1>
                
                <p className="text-gray-600 mb-6 text-lg">
                  {isError 
                    ? 'Please request a new verification email from your account settings.'
                    : alreadyVerified 
                      ? 'Your email address is already verified. You can continue using all features of your account.'
                      : 'Thank you for verifying your email address. Your account is now fully activated and you can access all features.'
                  }
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={() => navigate('/')}
                    className="bg-[#832EE6] text-white px-8 py-3 rounded-full font-semibold hover:bg-[#6B21A8] transition-colors"
                  >
                    Go to Homepage
                  </button>
                  <button
                    onClick={() => navigate('/my/settings')}
                    className="bg-white text-[#832EE6] border-2 border-[#832EE6] px-8 py-3 rounded-full font-semibold hover:bg-[#832EE6] hover:text-white transition-colors"
                  >
                    Go to Settings
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <Footer />
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.4s ease-out;
        }
      `}</style>
    </div>
  );
};

export default EmailVerified;
